import { DialogProps, Grid, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { FormDialog, SnackbarContext } from 'components';
import { Config } from 'config';
import { usePrevious } from 'hooks';
import { MenuSection, Shop } from 'interfaces';
import * as React from 'react';
import { MenuSectionService, ShopService } from 'services';

interface Props {
  dialogProps: DialogProps;
  menuSection?: MenuSection;
  menuSections: MenuSection[];
  refresh: () => void;
}

interface MenuSectionForm {
  title?: string;
  rank?: number;
  shopId?: number;
  image?: File;
}

export const SaveMenuSectionDialog: React.FC<Props> = ({
  dialogProps,
  menuSection,
  menuSections,
  refresh,
}) => {
  const snackbar = React.useContext(SnackbarContext);

  const [loading, setLoading] = React.useState(false);
  const [shops, setShops] = React.useState<Shop[]>([]);

  const getForm = React.useCallback(function getForm(
    menuSection?: MenuSection,
  ): MenuSectionForm {
    return {
      title: menuSection?.title ?? undefined,
      rank: menuSection?.rank ?? undefined,
      shopId: menuSection?.shopId ?? undefined,
    };
  },
  []);

  const [form, setForm] = React.useState(getForm(menuSection));

  const prevOpen = usePrevious(dialogProps.open);
  const justOpened = dialogProps.open && !prevOpen;

  React.useEffect(() => {
    setLoading(true);
    ShopService.getShops()
      .then((response: Shop[]) => {
        setShops(response);
      })
      .finally(() => setLoading(false));

    if (justOpened) {
      setForm(getForm(menuSection));
    }
  }, [getForm, menuSection, justOpened]);

  return (
    <FormDialog
      title={`${menuSection ? 'Edit' : 'Add'} Menu Section`}
      submitText="Save"
      onSubmit={submit}
      loading={loading}
      dialogProps={dialogProps}
    >
      <Grid container>
        <Grid item xs={12}>
          <TextField
            label="Title"
            type="text"
            fullWidth
            required
            onChange={(e) => handleChange(e)}
            name="title"
            value={form.title || ''}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            value={shops.find((s) => s.id === form.shopId) || null}
            options={shops}
            getOptionLabel={(option: Shop) => option?.title ?? ''}
            onChange={(e: React.ChangeEvent<{}>, shop: Shop | null) =>
              handleAutocompleteChange(shop?.id ?? null, 'shopId')
            }
            renderInput={(params) => (
              <TextField
                {...params}
                name="shop"
                variant="standard"
                label="Shop"
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Rank"
            type="number"
            fullWidth
            required
            onChange={(e) => handleChange(e)}
            name="rank"
            value={form.rank || ''}
          />
        </Grid>
        <Grid item xs={12}>
          {menuSection && menuSection.imageUrl && (
            <img
              src={`${Config.IMAGE_CDN_URL}${menuSection.imageUrl}`}
              alt=""
              style={{ height: 100, display: 'block' }}
            />
          )}
          <input
            id="icon-button-file"
            type="file"
            accept="image/*"
            onChange={onFileChange}
          />
        </Grid>
      </Grid>
    </FormDialog>
  );

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { currentTarget } = e;
    const { files } = currentTarget;

    if (!files || !files.length) {
      throw new Error('Select a file.');
    }

    const file = files[0];

    setForm((f) => ({ ...f, image: file }));
  }

  function handleAutocompleteChange(value: any, name: string) {
    setForm((f) => ({ ...f, [name]: value, rank: getNextRank(value) }));
  }

  function handleChange({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    try {
      const response = await MenuSectionService.save({
        id: menuSection?.id ?? undefined,
        data: form,
      });

      if (!response) {
        return;
      }

      if (dialogProps.onClose) {
        dialogProps.onClose(e);
      }

      refresh();
      snackbar.open('Menu section has been saved successfully.');
    } catch (error) {
      snackbar.error(error);
    }
  }

  function getNextRank(shopId?: number) {
    return (
      menuSections
        .filter((s) => !shopId || s.shopId === shopId)
        .reduce((prev, curr) => {
          if (curr.rank > prev) {
            return curr.rank;
          }

          return prev;
        }, 0) + 1
    );
  }
};
